import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Dumbbell, Calendar, History, ListChecks, Coffee, Play, Pause, MessageSquare, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import { AssignProgramDialog } from "./AssignProgramDialog";
import { WorkoutLogDetailDialog } from "./WorkoutLogDetailDialog";
import { useClientProgramAssignment } from "@/hooks/use-programs";

interface ClientWorkoutsProps {
  clientId: string;
  clientName: string;
}

interface WorkoutTemplate {
  id: string;
  name: string;
  description: string | null;
  estimated_duration_minutes: number | null;
  tags: string[];
}

interface ClientProgram {
  id: string;
  name: string | null;
  start_date: string | null;
  status: string;
  schedule: Record<string, boolean> | null;
  template: WorkoutTemplate;
  created_at: string;
}

interface WorkoutLog {
  id: string;
  started_at: string;
  completed_at: string | null;
  rating: number | null;
  notes: string | null;
  was_unscheduled?: boolean | null;
  template: {
    name: string;
  } | null;
}

const DAYS_OF_WEEK = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

export function ClientWorkouts({ clientId, clientName }: ClientWorkoutsProps) {
  const { organization } = useOrganization();
  const [programs, setPrograms] = useState<ClientProgram[]>([]);
  const [logs, setLogs] = useState<WorkoutLog[]>([]);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Form states
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [programName, setProgramName] = useState("");
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [schedule, setSchedule] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Multi-day program state
  const [assignProgramDialogOpen, setAssignProgramDialogOpen] = useState(false);
  const { assignment: programAssignment, loading: assignmentLoading, refetch: refetchAssignment } = useClientProgramAssignment(clientId);

  // Workout log detail (per-set comments)
  const [openLogId, setOpenLogId] = useState<string | null>(null);
  const [logCommentCounts, setLogCommentCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, [clientId]);

  // Real-time subscription for workout logs - trainers see client updates live
  useEffect(() => {
    if (!clientId) return;

    const channel = supabase
      .channel(`workout_logs_${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workout_logs',
          filter: `client_id=eq.${clientId}`,
        },
        () => {
          // Reload data when workout logs change
          loadData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId]);

  const loadData = async () => {
    try {
      // Load assigned programs
      const { data: programsData, error: programsError } = await supabase
        .from("client_programs")
        .select(`
          id,
          name,
          start_date,
          status,
          schedule,
          created_at,
          template:workout_templates(
            id,
            name,
            description,
            estimated_duration_minutes,
            tags
          )
        `)
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (programsError) throw programsError;

      // Filter out programs with null templates
      const validPrograms = (programsData || []).filter(
        (p) => p.template !== null
      ) as ClientProgram[];
      setPrograms(validPrograms);

      // Load workout logs. was_unscheduled drives a small badge so
      // the trainer can spot client-initiated extras at a glance
      // (CIW phase 4b).
      const { data: logsData, error: logsError } = await supabase
        .from("workout_logs")
        .select(`
          id,
          started_at,
          completed_at,
          rating,
          notes,
          was_unscheduled,
          template:workout_templates(name)
        `)
        .eq("client_id", clientId)
        .order("started_at", { ascending: false })
        .limit(20);

      if (logsError) throw logsError;
      const logRows = (logsData as WorkoutLog[]) || [];
      setLogs(logRows);

      // Comment counts per workout log (one-shot lookup; small N, history is limited to 20)
      try {
        const logIds = logRows.map((l) => l.id);
        if (logIds.length > 0) {
          const { data: setRows } = await supabase
            .from("set_logs")
            .select("id, workout_log_id")
            .in("workout_log_id", logIds);
          const setIds = (setRows || []).map((r: { id: string }) => r.id);
          const setToLog: Record<string, string> = {};
          (setRows || []).forEach((r: { id: string; workout_log_id: string }) => {
            setToLog[r.id] = r.workout_log_id;
          });
          if (setIds.length > 0) {
            const { data: cRows } = await (supabase as any)
              .from("set_log_comments")
              .select("set_log_id")
              .in("set_log_id", setIds);
            const counts: Record<string, number> = {};
            (cRows || []).forEach((c: { set_log_id: string }) => {
              const lid = setToLog[c.set_log_id];
              if (lid) counts[lid] = (counts[lid] || 0) + 1;
            });
            setLogCommentCounts(counts);
          } else {
            setLogCommentCounts({});
          }
        } else {
          setLogCommentCounts({});
        }
      } catch (e) {
        // Non-fatal — badges just won't show
        console.warn("Failed to load comment counts:", e);
      }

      // Load available templates
      let templatesQuery = supabase
        .from("workout_templates")
        .select("id, name, description, estimated_duration_minutes, tags");
      if (organization?.id) {
        templatesQuery = templatesQuery.eq("organization_id", organization.id);
      }
      const { data: templatesData, error: templatesError } = await templatesQuery
        .order("name");

      if (templatesError) throw templatesError;
      setTemplates(templatesData || []);
    } catch (error) {
      console.error("Error loading workout data:", error);
      toast.error("Failed to load workout data");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignProgram = async () => {
    if (!selectedTemplateId) {
      toast.error("Please select a workout template");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("client_programs").insert({
        client_id: clientId,
        template_id: selectedTemplateId,
        name: programName || null,
        start_date: startDate || null,
        status: "active",
        schedule: Object.keys(schedule).length > 0 ? schedule : null,
      });

      if (error) throw error;

      toast.success("Workout program assigned");
      setAssignDialogOpen(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error("Error assigning program:", error);
      toast.error(error.message || "Failed to assign program");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProgram = async (id: string) => {
    try {
      const { error } = await supabase
        .from("client_programs")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Program removed");
      setPrograms((prev) => prev.filter((p) => p.id !== id));
    } catch (error: any) {
      console.error("Error deleting program:", error);
      toast.error(error.message || "Failed to remove program");
    } finally {
      setDeleteId(null);
    }
  };

  const toggleDay = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleToggleProgramStatus = async () => {
    if (!programAssignment) return;

    try {
      const newStatus = programAssignment.status === "active" ? "paused" : "active";
      const { error } = await supabase
        .from("client_program_assignments")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", programAssignment.id);

      if (error) throw error;

      toast.success(newStatus === "active" ? "Program resumed" : "Program paused");
      refetchAssignment();
    } catch (error: any) {
      console.error("Error updating program status:", error);
      toast.error(error.message || "Failed to update program status");
    }
  };

  const handleRemoveProgramAssignment = async () => {
    if (!programAssignment) return;

    try {
      const { error } = await supabase
        .from("client_program_assignments")
        .update({ status: "completed", updated_at: new Date().toISOString() })
        .eq("id", programAssignment.id);

      if (error) throw error;

      toast.success("Program removed from client");
      refetchAssignment();
    } catch (error: any) {
      console.error("Error removing program:", error);
      toast.error(error.message || "Failed to remove program");
    }
  };

  const resetForm = () => {
    setSelectedTemplateId("");
    setProgramName("");
    setStartDate(format(new Date(), "yyyy-MM-dd"));
    setSchedule({});
  };

  const getScheduleDisplay = (schedule: Record<string, boolean> | null) => {
    if (!schedule) return "No schedule";
    const activeDays = DAYS_OF_WEEK.filter((d) => schedule[d.key])
      .map((d) => d.label)
      .join(", ");
    return activeDays || "No schedule";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Multi-Day Program Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Training Program
          </h3>
          {!programAssignment && (
            <Button
              size="sm"
              onClick={() => setAssignProgramDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Assign Program
            </Button>
          )}
        </div>

        {assignmentLoading ? (
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            </CardContent>
          </Card>
        ) : programAssignment ? (
          <Card className="border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-lg">
                      {programAssignment.program?.name}
                    </h4>
                    <Badge variant={programAssignment.status === "active" ? "default" : "secondary"}>
                      {programAssignment.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Day {programAssignment.current_day} of {programAssignment.program?.days_per_cycle}
                  </p>

                  {/* Current day preview */}
                  {programAssignment.program?.program_days && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {programAssignment.program.program_days.map((day) => (
                        <Badge
                          key={day.id}
                          variant={day.day_number === programAssignment.current_day ? "default" : "outline"}
                          className={`text-xs ${day.day_number === programAssignment.current_day ? "" : "opacity-60"}`}
                        >
                          {day.is_rest_day ? (
                            <Coffee className="h-3 w-3 mr-1" />
                          ) : (
                            <Dumbbell className="h-3 w-3 mr-1" />
                          )}
                          Day {day.day_number}
                        </Badge>
                      ))}
                    </div>
                  )}

                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleProgramStatus}
                    className="gap-1.5"
                  >
                    {programAssignment.status === "active" ? (
                      <>
                        <Pause className="h-3.5 w-3.5" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-3.5 w-3.5" />
                        Resume
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAssignProgramDialogOpen(true)}
                  >
                    Change
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive h-8 w-8"
                    onClick={handleRemoveProgramAssignment}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center">
              <ListChecks className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">
                No training program assigned. Assign a multi-day program to help {clientName} follow a structured workout plan.
              </p>
              <Button variant="outline" onClick={() => setAssignProgramDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Assign Program
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Workout Assignments — routed through AssignProgramDialog which writes
          to the canonical `client_program_assignments` table. The old
          `client_programs` path wrote to a different table that the mobile app
          doesn't read — clients never saw the workout. */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Individual Workouts</h3>
        <Button onClick={() => setAssignProgramDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Assign Workout
        </Button>
      </div>

      {programs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Dumbbell className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              No workout programs assigned yet
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setAssignProgramDialogOpen(true)}
            >
              Assign First Workout
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {programs.map((program) => (
            <Card key={program.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">
                        {program.name || program.template.name}
                      </h4>
                      <Badge
                        variant={
                          program.status === "active" ? "default" : "secondary"
                        }
                      >
                        {program.status}
                      </Badge>
                    </div>
                    {program.name && (
                      <p className="text-sm text-muted-foreground">
                        Template: {program.template.name}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {program.template.tags?.slice(0, 4).map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-xs capitalize"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      {program.template.estimated_duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {program.template.estimated_duration_minutes} min
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {getScheduleDisplay(program.schedule)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(program.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Workout History */}
      <div className="pt-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <History className="h-5 w-5" />
          Workout History
        </h3>
        {logs.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground">
              No workout history yet
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workout</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setOpenLogId(log.id)}
                    >
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {log.template?.name || "Workout"}
                          {log.was_unscheduled && (
                            <Badge
                              variant="secondary"
                              size="sm"
                              className="text-[9px] gap-0.5"
                              title="Client started this outside the program schedule"
                            >
                              <Sparkles className="h-2.5 w-2.5" />
                              Self-started
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {format(parseISO(log.started_at), "MMM d, yyyy h:mm a")}
                      </TableCell>
                      <TableCell>
                        {log.completed_at ? (
                          <Badge
                            variant="outline"
                            className="bg-success/10 text-success border-success/30"
                          >
                            Completed
                          </Badge>
                        ) : (
                          <Badge variant="secondary">In Progress</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.rating ? (
                          <span className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span
                                key={star}
                                className={
                                  star <= log.rating!
                                    ? "text-warning"
                                    : "text-muted-foreground/30"
                                }
                              >
                                ★
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {logCommentCounts[log.id] ? (
                          <Badge variant="secondary" className="gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {logCommentCounts[log.id]}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Add feedback
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Workout Program</DialogTitle>
            <DialogDescription>
              Assign a workout template to {clientName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template">Workout Template *</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a workout template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No workout templates found. Create one first in the Workouts
                  section.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="programName">
                Custom Name{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="programName"
                value={programName}
                onChange={(e) => setProgramName(e.target.value)}
                placeholder="e.g., John's Push Day"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Schedule{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={day.key}
                      checked={schedule[day.key] || false}
                      onCheckedChange={() => toggleDay(day.key)}
                    />
                    <label
                      htmlFor={day.key}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select days when this workout should be done
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignProgram} disabled={saving}>
              {saving ? "Assigning..." : "Assign Program"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Program?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this workout program from the client. Their
              workout history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDeleteProgram(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Multi-Day Program Dialog */}
      <AssignProgramDialog
        open={assignProgramDialogOpen}
        onOpenChange={setAssignProgramDialogOpen}
        onSuccess={() => {
          refetchAssignment();
        }}
        clientId={clientId}
        clientName={clientName}
      />

      {/* Per-set inline-comment dialog */}
      <WorkoutLogDetailDialog
        workoutLogId={openLogId}
        open={openLogId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setOpenLogId(null);
            // Refresh badges in case comments were added/removed
            loadData();
          }
        }}
      />
    </div>
  );
}

export default ClientWorkouts;
